import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type Schemas } from "../api/client";

type Capabilities = Schemas["WmsCapabilities"];

/** Public servers to try overlays with; they list EPSG:3857. */
const SUGGESTED = [
  {
    name: "NASA GIBS: satellite imagery, worldwide",
    url: "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi",
    layer: "MODIS_Terra_CorrectedReflectance_TrueColor",
    note: "Daily satellite images and other Earth observation layers from NASA, free to use. Over 1,000 layers; search for one by name. Opaque, so turn down its opacity on the map.",
  },
  {
    name: "terrestris: OpenStreetMap roads and labels",
    url: "https://ows.terrestris.de/osm/service",
    layer: "OSM-Overlay-WMS",
    note: "A free OpenStreetMap service; OSM-Overlay-WMS is transparent, so it draws roads and names over another map. Free use is limited to about 1:25,000; closer in it shows a watermark.",
  },
  {
    name: "British Geological Survey: 1:50,000 geology (UK)",
    url: "https://map.bgs.ac.uk/arcgis/services/BGS_Detailed_Geology/MapServer/WMSServer",
    layer: "",
    note: "Bedrock and superficial geology of Great Britain, under the Open Government Licence (Contains British Geological Survey materials © UKRI).",
  },
] as const;

/**
 * WMS overlays (design §9.2): read a server's capabilities, choose a layer,
 * add it. The server fetches the tiles, so only the addresses listed here
 * are ever reached.
 */
export function WmsOverlays() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["wms"],
    queryFn: () => unwrap(api.GET("/api/wms")),
  });
  const [url, setUrl] = useState("");
  const suggestion = SUGGESTED.find((x) => x.url === url);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ca, setCa] = useState("");
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [layer, setLayer] = useState("");
  const [style, setStyle] = useState("");
  const [format, setFormat] = useState("");
  const [name, setName] = useState("");
  const [transparent, setTransparent] = useState(true);

  const probe = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/wms/capabilities", {
          body: {
            url,
            username: username || null,
            password: password || null,
            ca_pem: ca || null,
          },
        }),
      ),
    onSuccess: (c) => {
      setCaps(c);
      const wanted = SUGGESTED.find((x) => x.url === url)?.layer;
      const first =
        c.layers.find((l) => l.web_mercator && l.name === wanted) ??
        c.layers.find((l) => l.web_mercator);
      setLayer(first?.name ?? "");
      setStyle("");
      setName(first?.title ?? "");
      setFormat(
        c.formats.find((f) => f === "image/png") ?? c.formats[0] ?? "image/png",
      );
    },
  });
  const chosen = caps?.layers.find((l) => l.name === layer);
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/wms", {
          body: {
            name,
            url,
            version: caps?.version === "1.1.1" ? "1.1.1" : "1.3.0",
            layers: layer,
            styles: style,
            format,
            transparent,
            crs: chosen?.web_mercator ?? "EPSG:3857",
            attribution: chosen?.attribution ?? caps?.title ?? null,
            username: username || null,
            password: password || null,
            ca_pem: ca || null,
            max_zoom: 19,
            cache_hours: 168,
          },
        }),
      ),
    onSuccess: () => {
      setCaps(null);
      setUrl("");
      setUsername("");
      setPassword("");
      setCa("");
      void qc.invalidateQueries({ queryKey: ["wms"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/wms/{id}", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["wms"] }),
  });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="WMS overlays"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Overlays from a map server (WMS)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Layers from a WMS server, shown over the base map. AVGV fetches them for
        everyone and keeps a copy of each tile for a week, so they still show
        when the server can&apos;t be reached. Only https:// addresses.
      </Typography>
      <Stack sx={{ gap: 2, mt: 1 }}>
        <TextField
          size="small"
          select
          label="Suggested servers"
          value={suggestion?.url ?? ""}
          onChange={(e) => {
            setUrl(e.target.value);
            setCaps(null);
          }}
          helperText={
            suggestion?.note ??
            "Public servers to try overlays with, or type your organisation's server below. Check a service's terms before relying on it in casework."
          }
        >
          <MenuItem value="">Your own server</MenuItem>
          {SUGGESTED.map((x) => (
            <MenuItem key={x.url} value={x.url}>
              {x.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Server address"
          placeholder="https://maps.example.org/wms"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Username (if needed)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
          />
          <TextField
            size="small"
            type="password"
            label="Password (if needed)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Stack>
        <TextField
          size="small"
          multiline
          minRows={2}
          label="CA certificate (PEM), for a server with its own certificates"
          value={ca}
          onChange={(e) => setCa(e.target.value)}
        />
        <Stack direction="row">
          <Button
            variant="outlined"
            disabled={!url.trim() || probe.isPending}
            onClick={() => probe.mutate()}
          >
            Read layers
          </Button>
        </Stack>
        {probe.isError && <Alert severity="error">{probe.error.message}</Alert>}
        {caps && (
          <Stack sx={{ gap: 2 }}>
            <Typography variant="body2">
              {caps.title || "WMS"} (version {caps.version}):{" "}
              {caps.layers.length} layers.
            </Typography>
            <Autocomplete
              size="small"
              options={caps.layers}
              value={chosen ?? null}
              getOptionLabel={(l) => `${l.title} (${l.name})`}
              getOptionDisabled={(l) => !l.web_mercator}
              isOptionEqualToValue={(a, b) => a.name === b.name}
              onChange={(_, l) => {
                setLayer(l?.name ?? "");
                setStyle("");
                setName(l?.title ?? "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Layer"
                  helperText="Type to search. Greyed-out layers aren't offered in Web Mercator."
                />
              )}
            />
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                select
                label="Style"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Default</MenuItem>
                {chosen?.styles.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Image format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {caps.formats
                  .filter((f) => f.startsWith("image/"))
                  .map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={transparent}
                    onChange={(e) => setTransparent(e.target.checked)}
                  />
                }
                label="Transparent background"
              />
            </Stack>
            <TextField
              size="small"
              label="Name shown on the map"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Stack direction="row">
              <Button
                variant="contained"
                disabled={!layer || !name.trim() || add.isPending}
                onClick={() => add.mutate()}
              >
                Add overlay
              </Button>
            </Stack>
            {add.isError && <Alert severity="error">{add.error.message}</Alert>}
          </Stack>
        )}
      </Stack>
      {(list.data?.length ?? 0) > 0 && (
        <TableContainer sx={{ mt: 2 }}>
          <Table size="small" aria-label="Overlays">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Server</TableCell>
                <TableCell>Layer</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data?.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{o.name}</TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>
                    {o.settings?.url}
                    {o.settings?.has_password && " (with a password)"}
                  </TableCell>
                  <TableCell>{o.settings?.layers}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete the overlay "${o.name}"?`))
                          remove.mutate(o.id);
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
